import {
  CustomSubscription,
  CustomSubscriptionType,
  getTypedCustomSubscriptionValueForRefetch,
  InvalidationMessage,
  OnCreateCustomSubscriptionSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnUpdateCustomSubscriptionSubscription,
  onCreateCustomSubscription,
  onDeleteCustomSubscription,
  onUpdateCustomSubscription,
  parseInvalidationMessage,
} from '@rentguru/commons-utils';
import { useSubscriptions } from '@up2rent/graphql';
import { isNil } from 'lodash';
import { useRef } from 'react';

import { useUser } from './UserContext';

// Receiver side of the Dashboard V2 invalidation bus (docs/specs/00-architecture.md §4).
interface InvalidationBusOptions {
  onLegacyRefetch?: (table: string) => void;
  onRejected?: (error: Error) => void;
}

export const useInvalidationBus = (
  onMessage: (message: InvalidationMessage) => void,
  options: InvalidationBusOptions = {}
) => {
  const { clientId } = useUser();
  const clientIdRef = useRef<string | null>(clientId);
  clientIdRef.current = clientId;
  const onMessageRef = useRef<(message: InvalidationMessage) => void>(onMessage);
  onMessageRef.current = onMessage;
  const optionsRef = useRef<InvalidationBusOptions>(options);
  optionsRef.current = options;

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const customSubscription = data.onCreateCustomSubscription as CustomSubscription;
      if (customSubscription.name !== CustomSubscriptionType.REFETCH) {
        return;
      }
      const message = parseInvalidationMessage(customSubscription);
      if (isNil(message)) {
        optionsRef.current.onLegacyRefetch?.(getTypedCustomSubscriptionValueForRefetch(customSubscription).table);
        return;
      }
      if (!isNil(clientIdRef.current) && message.wsId !== clientIdRef.current) {
        return;
      }
      onMessageRef.current(message);
    },
    () => {},
    () => {},
    (error) => optionsRef.current.onRejected?.(error)
  );
};
